import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Space, message } from 'antd';
import { DEFAULT_CURRENCY } from '@/constants/currency';
import type { RateCard } from '@/models/rateCard';
import type { ColumnType } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { NAMESPACE } from '@/constants';
import { COLUMNS, Column } from '../RateCards.constants';
import type {
  UseRateCards,
  RateCardListColumns,
  BaseColumn,
  HandleTableChange,
  HandleValuesChange,
  RateCardRowData
} from '../RateCards.types';
import { useCreateRateCardMutation, useGetRateCardListQuery, useUpdateRateCardMutation } from '@/api';
import { usePagination } from '@/hooks/usePagination';
import { Tag } from '@/components';
import { Actions } from '../components';
import { useSorting } from '@/hooks/useSorting';
import { SortRateCardList } from '@/models';
import { EditableCellProps } from '../components/EditableCell/EditableCell.types';
import { useRateCardOptions } from './useRateCardOptions';
import { toRateCardFormValues } from '../toRateCardFormValues';

const { PrimaryTag, ChannelTag } = Tag;
const TEMP_ID = 'temp-id';

const coverWithTag = (data?: string | string[]) => {
  if (!Array.isArray(data)) return data ?? 'N/A';
  if (!data.length) return 'N/A';

  return (
    <Space size={5} wrap>
      {data.map((item) => (
        <PrimaryTag key={item}>{item}</PrimaryTag>
      ))}
    </Space>
  );
};

export const useRateCards: UseRateCards = () => {
  const [form] = Form.useForm();
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [formData, setFormData] = useState<RateCardRowData>();
  const [rateCardList, setRateCardList] = useState<RateCardRowData[]>([]);
  const [editingId, setEditingId] = useState<string>();
  const [editingRecord, setEditingRecord] = useState<Partial<RateCard>>();
  const cancel = useCallback(() => {
    form.resetFields();
    setEditingId(undefined);
    setEditingRecord(undefined);
    setFormData(undefined);
    setIsCreating(false);
    setRateCardList((prevValue) => prevValue.filter(({ _id }) => _id !== TEMP_ID));
  }, [form]);
  const { limit, page, handlePaginationChange, resetPage } = usePagination({
    page: 1,
    limit: 10,
    onChange: cancel
  });
  const { sort, order, handleSort } = useSorting<SortRateCardList>({ onSort: resetPage, order: 1 });
  const [filters, setFilters] = useState<Record<string, FilterValue | null>>();
  const { filterOptions, rateCardOptions } = useRateCardOptions({ ...formData });
  const {
    data: rateCards,
    isFetching,
    isLoading: areRateCardsLoading
  } = useGetRateCardListQuery({ limit, page, sort, order, ...filters });
  const [updateRateCard, { data: updatedRateCard, isLoading: isRateCardUpdating }] =
    useUpdateRateCardMutation();
  const [createRateCard, { data: createdRateCard, isLoading: isRateCardCreating }] =
    useCreateRateCardMutation();
  const { t } = useTranslation(NAMESPACE.RATE_CARDS_PAGE);

  useEffect(() => {
    if (updatedRateCard || createdRateCard) {
      cancel();
      message.success({
        content: updatedRateCard ? t('rateCardHasBeenEdited') : t('rateCardHasBeenCreated'),
        duration: 3
      });
    }
  }, [t, updatedRateCard, createdRateCard, form, cancel]);

  const isEditing = useCallback((id: string) => id === editingId, [editingId]);

  const handleUpdateRateCard = useCallback(
    (record: RateCardRowData) => {
      const values = toRateCardFormValues(record);
      setFormData({ ...record, ...values } as RateCardRowData);
      setEditingRecord(values);
      setEditingId(record._id);
    },
    []
  );

  useEffect(() => {
    if (editingId && editingRecord) {
      form.setFieldsValue(editingRecord);
    }
  }, [editingId, editingRecord, form]);

  const handleCreateRateCard = () => {
    setIsCreating(true);
    const values = toRateCardFormValues({ currency: DEFAULT_CURRENCY });
    const draft = { _id: TEMP_ID, key: TEMP_ID, ...values } as RateCardRowData;
    setFormData(draft);
    setEditingRecord(values);
    setEditingId(TEMP_ID);
    setRateCardList((prevState) => [draft, ...prevState]);
  };

  const saveRateCard = useCallback(
    async (id: string) => {
      try {
        const values = await form.validateFields();
        const payload = { ...values, currency: values.currency ?? DEFAULT_CURRENCY };
        if (id === TEMP_ID) {
          await createRateCard(payload).unwrap();
        } else {
          await updateRateCard({ _id: id, ...payload }).unwrap();
        }
      } catch (error) {
        const formError = error as { errorFields?: unknown[] };
        if (formError.errorFields?.length) {
          message.error(t('fixValidationErrors'));
          return;
        }
        const apiError = error as { data?: { message?: string } };
        const errorMessage = apiError.data?.message ?? (error as Error).message;
        if (errorMessage) {
          message.error(errorMessage);
        }
      }
    },
    [createRateCard, form, t, updateRateCard]
  );

  const createEditableColumn = useCallback(
    ({ i18nKey, editable: _editable, rules, valuePropName, selectOptions, ...rest }: BaseColumn): ColumnType<RateCardRowData> => ({
      ...rest,
      title: t(i18nKey),
      onCell: (record) => {
        const { type, dataIndex, optionsKey } = rest;
        return {
          type,
          rules,
          record,
          dataIndex,
          valuePropName,
          isEditing: isEditing(record._id),
          ...(selectOptions
            ? { options: selectOptions }
            : rateCardOptions &&
                optionsKey && {
                  options: rateCardOptions[optionsKey]?.map((option) => ({ label: option, value: option }))
                })
        } as EditableCellProps as ReturnType<NonNullable<ColumnType<RateCardRowData>['onCell']>>;
      }
    }),
    [isEditing, rateCardOptions, t]
  );

  const createNonEditableColumn = useCallback(
    ({ i18nKey, ...rest }: BaseColumn) => ({
      ...rest,
      title: t(i18nKey)
    }),
    [t]
  );

  const columns = useMemo((): RateCardListColumns => {
    if (filterOptions) {
      COLUMNS[Column.CHANNEL].filters = filterOptions.channels?.map((channel) => ({
        text: channel,
        value: channel
      }));
    }
    COLUMNS[Column.FORMAT].render = (_, { formats }) => coverWithTag(formats);
    COLUMNS[Column.BUYING_METRIC].render = (_, { buyingMetric }) => coverWithTag(buyingMetric);
    COLUMNS[Column.MEASURE].render = (_, { kpis }) => coverWithTag(kpis);
    COLUMNS[Column.CHANNEL].render = (_, { channel }) =>
      channel ? <ChannelTag>{channel}</ChannelTag> : 'N/A';
    COLUMNS[Column.ACTIONS].render = (_, record) => {
      const { _id } = record;
      const isNewRateCard = _id === TEMP_ID;
      const handleSaveRateCard = () => saveRateCard(_id);
      const handleEditRateCard = () => handleUpdateRateCard(record);

      return (
        <Actions
          id={_id}
          isEditing={isEditing(_id)}
          isNewRateCard={isNewRateCard}
          cancel={cancel}
          saveRateCard={handleSaveRateCard}
          editRateCard={handleEditRateCard}
          isLoading={isRateCardUpdating || isRateCardCreating}
        />
      );
    };

    return Object.values(COLUMNS).map((column) =>
      column.editable ? createEditableColumn(column) : createNonEditableColumn(column)
    );
  }, [
    filterOptions,
    isEditing,
    cancel,
    isRateCardUpdating,
    isRateCardCreating,
    saveRateCard,
    handleUpdateRateCard,
    createEditableColumn,
    createNonEditableColumn
  ]);

  useEffect(() => {
    if (rateCards) {
      setRateCardList(rateCards.data.map((rateCard) => ({ key: rateCard._id, ...rateCard })));
    }
  }, [rateCards]);

  const handleTableChange: HandleTableChange = (_, filtersMap, sorter) => {
    if (!Array.isArray(sorter)) {
      handleSort(sorter as unknown as SorterResult<SortRateCardList>);
    }

    setFilters(filtersMap);
  };

  const handleFormChange: HandleValuesChange = (_, values) => setFormData(values);

  const rateCardsNumber = useMemo(() => {
    if (rateCards) {
      const { count } = rateCards;
      return isCreating ? count + 1 : count;
    }
    return undefined;
  }, [isCreating, rateCards]);

  return {
    form,
    filters,
    editingId,
    handleTableChange,
    handleFormChange,
    handleCreateRateCard,
    columns,
    isFetching,
    isLoading: areRateCardsLoading,
    rateCardList,
    handlePaginationChange,
    page,
    limit,
    count: rateCardsNumber
  };
};
